import { api } from "../api";
import { ApiResponse, League, Sport, SportName, Roster } from "@/types/sports";

// Caching layer to prevent redundant API calls
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresIn: number; // milliseconds
}

class RequestCache {
  private cache: Map<string, CacheEntry<unknown>> = new Map();

  set<T>(key: string, data: T, expiresIn: number = 60 * 1000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresIn,
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    // Check if cache entry has expired
    if (Date.now() - entry.timestamp > entry.expiresIn) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  has(key: string): boolean {
    return this.get<unknown>(key) !== null;
  }

  clear(): void {
    this.cache.clear();
  }
}

/**
 * Service for sports and leagues related operations
 */
export class SportService {
  private requestCache = new RequestCache();

  /**
   * Get sports data from cache only (doesn't make an API call)
   * Returns null if no cache is available
   */
  getCachedSports(): ApiResponse<Sport[]> | null {
    const cacheKey = "getAllSports";
    const response = this.requestCache.get<ApiResponse<Sport[]>>(cacheKey);
    console.log("response", response);
    return response;
  }

  /**
   * Get all available sports
   */
  async getAllSports(): Promise<ApiResponse<Sport[]>> {
    const cacheKey = "getAllSports";

    const cachedData = this.requestCache.get<ApiResponse<Sport[]>>(cacheKey);
    if (cachedData) {
      console.log("Returning sports data from cache");
      return cachedData;
    }
    const response = await api.get<Sport[]>("/sports");

    // Check if the data has a nested data structure and fix it
    if (response.success && response.data && typeof response.data === 'object' && 'data' in response.data) {
      // Handle nested data structure
      const normalizedResponse: ApiResponse<Sport[]> = {
        success: response.success,
        data: response.data.data as Sport[]
      };
      
      // Cache the normalized response
      this.requestCache.set(cacheKey, normalizedResponse, 5 * 60 * 1000); // Cache for 5 minutes
      return normalizedResponse;
    }

    // Cache successful responses (if not already handled above)
    if (response.success) {
      this.requestCache.set(cacheKey, response, 5 * 60 * 1000); // Cache for 5 minutes
    }

    return response;
  }

  /**
   * Get a specific sport by name
   */
  async getSportByName(name: SportName): Promise<ApiResponse<Sport>> {
    const cacheKey = `getSportByName:${name}`;

    // Return from cache if available
    const cachedData = this.requestCache.get<ApiResponse<Sport>>(cacheKey);
    if (cachedData) return cachedData;

    // Otherwise, make the API call
    const response = await api.get<Sport>(`/sports/${name.toLowerCase()}`);

    // Cache successful responses
    if (response.success) {
      this.requestCache.set(cacheKey, response, 5 * 60 * 1000); // Cache for 5 minutes
    }

    return response;
  }

  /**
   * Get all leagues for a user
   */
  async getUserLeagues(): Promise<ApiResponse<League[]>> {
    return api.get<League[]>("/leagues");
  }

  /**
   * Get leagues for a specific sport
   */
  async getLeaguesBySport(
    sportName: SportName
  ): Promise<ApiResponse<League[]>> {
    return api.get<League[]>(`/leagues?sport=${sportName}`);
  }

  /**
   * Get a specific league by ID
   */
  async getLeagueById(leagueId: string): Promise<ApiResponse<League>> {
    return api.get<League>(`/leagues/${leagueId}`);
  }

  /**
   * Get roster for a specific league
   */
  async getLeagueRoster(leagueId: string): Promise<ApiResponse<Roster>> {
    // First check authentication status
    try {
      // Use the /api/auth/me endpoint to check if the user is authenticated
      const authCheck = await fetch('/api/auth/me');
      if (!authCheck.ok) {
        return {
          success: false,
          error: "User is not authenticated",
          data: null as unknown as Roster,
        };
      }
      
      // User is authenticated, proceed with the roster request
      return api.get<Roster>(`/leagues/${leagueId}/roster`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch roster",
        data: null as unknown as Roster,
      };
    }
  }

  /**
   * Join a league
   */
  async joinLeague(
    leagueId: string,
    teamName: string
  ): Promise<ApiResponse<League>> {
    return api.post<League>(`/leagues/${leagueId}/join`, { teamName });
  }

  /**
   * Create a new league
   */
  async createLeague(
    leagueData: Partial<League>
  ): Promise<ApiResponse<League>> {
    return api.post<League>("/leagues", leagueData);
  }
}

// Create a singleton instance for use throughout the app
export const sportService = new SportService();
